import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const TYPE_LABELS = {
  journal_text: 'Message terrain',
  journal_vocal: 'Vocal transcrit',
  journal_photo: 'Photo terrain',
  reserve: 'Réserve',
  tache: 'Action',
  facture: 'Facture',
  point_crc: 'Point du CR précédent',
};

const SOURCE_LABELS = {
  jarvis_whatsapp_import: 'WhatsApp',
  terrain: 'Terrain',
  bureau: 'Bureau',
};

// Regex globale pour matcher [src:TYPE:ID, src:TYPE:ID, ...]
const SRC_REGEX = /\[(?:src:[^\],]+(?:,\s*src:[^\],]+)*)\]/g;

function buildCitations(rawTag) {
  // rawTag = "[src:TYPE:ID, src:TYPE:ID]"
  const inner = rawTag.slice(1, -1); // retire crochets
  return inner.split(',').map((s) => {
    const trimmed = s.trim().replace(/^src:/, '');
    const idx = trimmed.indexOf(':');
    if (idx < 0) return null;
    return { type: trimmed.slice(0, idx), id: trimmed.slice(idx + 1) };
  }).filter(Boolean);
}

function CitationSup({ citations, sourcesIndex }) {
  return citations.map((c, idx) => {
    const source = sourcesIndex?.[c.id];
    if (!source) {
      return (
        <sup key={`miss-${idx}`} className="text-gray-400 text-[10px] ml-0.5" data-testid="crc-source-missing">[?]</sup>
      );
    }
    const label = idx === 0 ? (source.date || '·') : `+${idx}`;
    return (
      <TooltipProvider key={`src-${idx}`} delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <sup
              className="text-orange-600 cursor-help font-medium ml-0.5 hover:text-orange-800"
              data-testid={`crc-source-${c.id}`}
            >
              [{label}]
            </sup>
          </TooltipTrigger>
          <TooltipContent className="max-w-md p-3" side="top">
            <div className="text-xs font-semibold mb-1 flex items-center gap-2">
              <span>{TYPE_LABELS[c.type] || c.type}</span>
              {source.source && (
                <span className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                  {SOURCE_LABELS[source.source] || source.source}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-700 mb-1">{source.preview}</div>
            {source.author && <div className="text-[11px] text-gray-500">— par {source.author}</div>}
            {source.lot && <div className="text-[11px] text-gray-500">Lot : {source.lot}</div>}
            {source.date && <div className="text-[11px] text-gray-400 mt-1">{source.date}</div>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  });
}

// Transforme une chaîne de texte en remplaçant les [src:TYPE:ID] par des composants tooltip.
function processTextString(text, sourcesIndex) {
  if (!text || typeof text !== 'string' || !sourcesIndex) return text;
  if (!text.includes('[src:')) return text;

  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  // Reset regex stateful counter
  SRC_REGEX.lastIndex = 0;
  while ((match = SRC_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    const citations = buildCitations(match[0]);
    parts.push(
      <CitationSup key={`cit-${key++}`} citations={citations} sourcesIndex={sourcesIndex} />
    );
    lastIndex = SRC_REGEX.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.substring(lastIndex));
  return parts.length > 0 ? parts : text;
}

// Walk recursive : traite tous les enfants string d'un node, en gardant la structure des éléments React.
function walkChildren(children, sourcesIndex) {
  if (!sourcesIndex) return children;
  const arr = React.Children.toArray(children);
  return arr.map((child, i) => {
    if (typeof child === 'string') {
      const processed = processTextString(child, sourcesIndex);
      // Si processed est un tableau d'éléments, le wrap dans un fragment avec key
      if (Array.isArray(processed)) {
        return <React.Fragment key={`txt-${i}`}>{processed}</React.Fragment>;
      }
      return processed;
    }
    if (React.isValidElement(child) && child.props?.children) {
      return React.cloneElement(child, {
        ...child.props,
        children: walkChildren(child.props.children, sourcesIndex),
      });
    }
    return child;
  });
}

const buildComponents = (sourcesIndex) => ({
  h1: ({ children, ...props }) => (
    <h1
      className="mt-6 mb-3 pb-2 border-b-2 border-[#C4773B] text-[#1E3A5F]"
      style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Serif Display, serif' }}
      {...props}
    >
      {walkChildren(children, sourcesIndex)}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2
      className="mt-6 mb-2 text-[#1E3A5F]"
      style={{ fontSize: 17, fontWeight: 700 }}
      {...props}
    >
      {walkChildren(children, sourcesIndex)}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3
      className="mt-4 mb-1 text-[#2C2C2C]"
      style={{ fontSize: 14, fontWeight: 600 }}
      {...props}
    >
      {walkChildren(children, sourcesIndex)}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className="text-sm font-bold text-[#2C2C2C] mt-3 mb-1" {...props}>
      {walkChildren(children, sourcesIndex)}
    </h4>
  ),
  p: ({ children, ...props }) => (
    <p className="text-sm text-[#2C2C2C] my-2 leading-relaxed" {...props}>
      {walkChildren(children, sourcesIndex)}
    </p>
  ),
  ul: ({ ...props }) => <ul className="list-disc ml-6 space-y-1 my-2 text-sm text-[#2C2C2C]" {...props} />,
  ol: ({ ...props }) => <ol className="list-decimal ml-6 space-y-1 my-2 text-sm text-[#2C2C2C]" {...props} />,
  li: ({ children, ...props }) => (
    <li className="text-sm text-[#4A4A4A]" {...props}>
      {walkChildren(children, sourcesIndex)}
    </li>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-[#1E3A5F]" {...props}>
      {walkChildren(children, sourcesIndex)}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em className="italic text-[#4A4A4A]" {...props}>
      {walkChildren(children, sourcesIndex)}
    </em>
  ),
  table: ({ ...props }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: ({ ...props }) => <thead {...props} />,
  tbody: ({ ...props }) => <tbody {...props} />,
  tr: ({ ...props }) => <tr className="border-b border-[#E0E0DB]" {...props} />,
  th: ({ ...props }) => (
    <th
      className="text-left px-3 py-2 font-bold text-white"
      style={{ background: '#1E3A5F', fontSize: 12 }}
      {...props}
    />
  ),
  td: ({ children, ...props }) => (
    <td className="px-3 py-2 align-top border border-[#E0E0DB]" {...props}>
      {walkChildren(children, sourcesIndex)}
    </td>
  ),
  a: ({ ...props }) => <a className="text-[#C4773B] hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
  blockquote: ({ ...props }) => (
    <blockquote className="border-l-4 border-[#C4773B] pl-4 italic text-[#6B6B6B] my-3" {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="bg-[#F5F5F3] px-1 py-0.5 rounded text-xs text-[#C4773B] font-mono" {...props} />
    ) : (
      <code className="block bg-[#F5F5F3] p-3 rounded text-xs font-mono overflow-x-auto my-3" {...props} />
    ),
  hr: () => <hr className="my-6 border-t border-[#E0E0DB]" />,
});

export default function CRCMarkdownRenderer({ md, photosMeta, sourcesIndex }) {
  const safe = md || '';
  if (!safe.trim()) {
    return <p className="text-sm italic text-[#9A9A9A]">Aucun contenu</p>;
  }
  const components = buildComponents(sourcesIndex);
  return (
    <div className="crc-markdown-rendered" data-testid="crc-markdown-rendered">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {safe}
      </ReactMarkdown>

      {Array.isArray(photosMeta) && photosMeta.length > 0 && (
        <section className="mt-8" data-testid="crc-photos-annexe">
          <h2
            className="mt-6 mb-3 pb-2 border-b-2 border-[#C4773B] text-[#1E3A5F]"
            style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Serif Display, serif' }}
          >
            Annexes photographiques
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {photosMeta.map((ph, i) => {
              const url = ph.url || ph.media_url || '';
              const date = (ph.date || ph.created_at || '').slice(0, 10);
              const caption = ph.caption || ph.content || `Photo ${i + 1}`;
              return (
                <figure
                  key={ph.id || i}
                  className="border border-[#E0E0DB] bg-white overflow-hidden"
                  data-testid={`crc-photo-annexe-${i}`}
                >
                  <img
                    src={url}
                    alt={caption}
                    className="w-full object-cover"
                    style={{ height: 200 }}
                    loading="lazy"
                  />
                  <figcaption className="px-3 py-2 bg-[#FAFAF8] text-xs">
                    <p className="font-semibold text-[#2C2C2C] mb-0.5 truncate">{caption}</p>
                    <p className="text-[#6B6B6B]">
                      {date}
                      {ph.lot && ` — ${ph.lot}`}
                    </p>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
